import { Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Button } from '@/components/ui/button';
import type { LineItemInput } from '@everplan/shared';

interface LineItemRowProps {
  item: Omit<LineItemInput, 'sortOrder'>;
  onChange: (updated: Omit<LineItemInput, 'sortOrder'>) => void;
  onRemove: () => void;
}

function formatDollars(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

export function LineItemRow({ item, onChange, onRemove }: LineItemRowProps) {
  const total = item.quantity != null ? item.quantity * item.unitCost : item.unitCost;

  return (
    <tr className="group border-b border-border/30 last:border-0">
      <td className="py-1 pr-2 align-top">
        <textarea
          value={item.name}
          onChange={(e) => onChange({ ...item, name: e.target.value })}
          placeholder="Item name"
          rows={1}
          className="w-full resize-none overflow-hidden rounded-md border border-transparent bg-transparent px-2 py-1 text-xs hover:border-border focus:border-border focus:outline-none focus:ring-1 focus:ring-ring field-sizing-content"
        />
      </td>
      <td className="py-1 pr-2 w-16">
        <Input
          type="number"
          min={1}
          value={item.quantity ?? ''}
          onChange={(e) => {
            const val = e.target.value;
            onChange({
              ...item,
              quantity: val === '' ? null : parseInt(val, 10) || null,
            });
          }}
          placeholder="—"
          className="h-7 w-16 text-sm text-right border-transparent bg-transparent hover:border-border focus:border-border [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
      </td>
      <td className="py-1 pr-2 w-24">
        <CurrencyInput
          valueCents={item.unitCost}
          onChangeCents={(cents) =>
            onChange({ ...item, unitCost: cents ?? 0 })
          }
          placeholder="0.00"
          className="h-7 w-24 border-transparent bg-transparent hover:border-border focus:border-border"
        />
      </td>
      <td className="py-1 w-20 text-right text-sm tabular-nums text-muted-foreground">
        {formatDollars(total)}
      </td>
      <td className="py-1 pl-1 w-6">
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          onClick={onRemove}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="size-3" />
        </Button>
      </td>
    </tr>
  );
}
